(function () {
  var existing = window.ZasuwaInsights;

  if (existing && existing.__initialized) {
    return;
  }

  var sdk = existing || {};
  var productObserver = null;
  var productStates = new WeakMap();
  var observedProducts = new WeakSet();
  var productImpressionKeys = new Set();
  var productClickListenerBound = false;
  var initialScanScheduled = false;
  var cartFetchWrapped = false;
  var cartRefreshTimer = null;
  var cartBaselineStarted = false;
  var batchFlushTimer = null;
  var batchFlushInFlight = false;
  var batchDataGeneration = 0;
  var leaveBatchHandled = false;
  var privacyListenersBound = false;
  var privacyFeatureRequested = false;
  var privacyAnalyticsState = null;
  var privacyRetryTimer = null;
  var privacyRetryAttempt = 0;
  var visitorIdStorageKey = 'zasuwa_insights_visitor_id';
  var batchFlushIntervalMs = 30000;
  var batchFlushThreshold = 50;
  var batchFetchTimeoutMs = 15000;
  var privacyRetryDelays = [5000, 15000, 30000];

  function generateUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    var now = Date.now();
    var perf = window.performance && typeof window.performance.now === 'function' ? window.performance.now() : 0;
    var seed = now + perf;

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (char) {
      var random = (seed + Math.random() * 16) % 16 | 0;
      seed = Math.floor(seed / 16);
      return (char === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    });
  }

  function getLocalStorageVisitorId() {
    try {
      return window.localStorage.getItem(visitorIdStorageKey) || '';
    } catch (error) {
      return '';
    }
  }

  function saveLocalStorageVisitorId(visitorId) {
    try {
      window.localStorage.setItem(visitorIdStorageKey, visitorId);
    } catch (error) {
      return false;
    }

    return true;
  }

  function resolveVisitorId() {
    if (typeof sdk.visitorId === 'string' && sdk.visitorId) {
      return sdk.visitorId;
    }

    var storedVisitorId = getLocalStorageVisitorId();

    if (storedVisitorId) {
      sdk.visitorId = storedVisitorId;
      return sdk.visitorId;
    }

    sdk.visitorId = generateUuid();
    saveLocalStorageVisitorId(sdk.visitorId);

    return sdk.visitorId;
  }

  function getCustomerPrivacyApi() {
    if (!window.Shopify || !window.Shopify.customerPrivacy) {
      return null;
    }

    return window.Shopify.customerPrivacy;
  }

  function readAnalyticsAllowed() {
    try {
      var customerPrivacy = getCustomerPrivacyApi();

      if (!customerPrivacy) {
        return null;
      }

      if (typeof customerPrivacy.analyticsProcessingAllowed === 'function') {
        var analyticsProcessingAllowed = customerPrivacy.analyticsProcessingAllowed();
        return typeof analyticsProcessingAllowed === 'boolean' ? analyticsProcessingAllowed : null;
      }

      if (typeof customerPrivacy.userCanBeTracked === 'function') {
        var userCanBeTracked = customerPrivacy.userCanBeTracked();
        return typeof userCanBeTracked === 'boolean' ? userCanBeTracked : null;
      }

      if (typeof customerPrivacy.currentVisitorConsent === 'function') {
        var visitorConsent = customerPrivacy.currentVisitorConsent() || {};
        if (visitorConsent.analytics === 'yes') {
          return true;
        }
        if (visitorConsent.analytics === 'no') {
          return false;
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  function stopPrivacyRetries() {
    if (privacyRetryTimer) {
      clearTimeout(privacyRetryTimer);
      privacyRetryTimer = null;
    }
  }

  function schedulePrivacyRetry() {
    privacyFeatureRequested = false;

    if (privacyRetryTimer || privacyRetryAttempt >= privacyRetryDelays.length) {
      return;
    }

    var retryDelay = privacyRetryDelays[privacyRetryAttempt];
    privacyRetryAttempt += 1;
    privacyRetryTimer = window.setTimeout(function () {
      privacyRetryTimer = null;
      requestPrivacyApi();
    }, retryDelay);
  }

  function clearPendingTrackingData() {
    batchDataGeneration += 1;
    leaveBatchHandled = false;

    if (Array.isArray(sdk.queue)) {
      sdk.queue.length = 0;
    } else {
      sdk.queue = [];
    }

    if (Array.isArray(sdk.cartSnapshots)) {
      sdk.cartSnapshots.length = 0;
    } else {
      sdk.cartSnapshots = [];
    }

    if (cartRefreshTimer) {
      clearTimeout(cartRefreshTimer);
      cartRefreshTimer = null;
    }
  }

  function updateAnalyticsAllowed(options) {
    var previousAllowed = sdk.analyticsAllowed === true;

    privacyAnalyticsState = readAnalyticsAllowed();
    sdk.analyticsAllowed = privacyAnalyticsState === true;

    if (previousAllowed === true && privacyAnalyticsState === false && options && options.clearOnRevoke === true) {
      clearPendingTrackingData();
    }

    if (privacyAnalyticsState !== null) {
      stopPrivacyRetries();
    }

    return privacyAnalyticsState;
  }

  function bindPrivacyListeners() {
    if (privacyListenersBound) {
      return;
    }

    privacyListenersBound = true;

    document.addEventListener('visitorConsentCollected', function () {
      updateAnalyticsAllowed({ clearOnRevoke: true });
    });
    document.addEventListener('trackingConsentAccepted', function () {
      updateAnalyticsAllowed();
    });
    document.addEventListener('shopifyCustomerPrivacyApiLoaded', function () {
      updateAnalyticsAllowed();
    });
  }

  function requestPrivacyApi() {
    if (privacyFeatureRequested) {
      return;
    }

    privacyFeatureRequested = true;

    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      privacyFeatureRequested = false;
      if (updateAnalyticsAllowed() === null) {
        schedulePrivacyRetry();
      }
      return;
    }

    try {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        function (error) {
          privacyFeatureRequested = false;

          if (error) {
            privacyAnalyticsState = null;
            sdk.analyticsAllowed = false;
            schedulePrivacyRetry();
            return;
          }

          if (updateAnalyticsAllowed() === null) {
            schedulePrivacyRetry();
          }
        }
      );
    } catch (error) {
      privacyFeatureRequested = false;
      privacyAnalyticsState = null;
      sdk.analyticsAllowed = false;
      schedulePrivacyRetry();
    }
  }

  function getBatchApiUrl() {
    var context = sdk.getContext();
    var config = context.config || {};
    var apiUrl = typeof config.api_url === 'string' ? config.api_url.trim() : '';

    return apiUrl;
  }

  function getPendingBatchCount() {
    var queueLength = Array.isArray(sdk.queue) ? sdk.queue.length : 0;
    var cartSnapshotLength = Array.isArray(sdk.cartSnapshots) ? sdk.cartSnapshots.length : 0;

    return queueLength + cartSnapshotLength;
  }

  function ensureBatchTimer() {
    if (batchFlushTimer || typeof window.setInterval !== 'function') {
      return;
    }

    batchFlushTimer = window.setInterval(function () {
      sdk.flush();
    }, batchFlushIntervalMs);
  }

  function canSendBatch() {
    var context = sdk.getContext();
    var config = context.config || {};

    if (config.enabled !== true) {
      return false;
    }

    if (sdk.isAnalyticsAllowed() !== true) {
      return false;
    }

    if (!getBatchApiUrl()) {
      return false;
    }

    if (!resolveVisitorId()) {
      return false;
    }

    return true;
  }

  function removeSentPrefix(collection, count) {
    if (!Array.isArray(collection) || count <= 0) {
      return;
    }

    collection.splice(0, Math.min(count, collection.length));
  }

  function buildBatchPayload(events, cartSnapshots) {
    var context = sdk.getContext();

    return {
      site_key: context.site_key || '',
      shop_domain: context.shop_domain || '',
      visitor_id: resolveVisitorId(),
      sent_at: new Date().toISOString(),
      events: events,
      cart_snapshots: cartSnapshots,
    };
  }

  function hasPendingBatchData() {
    return getPendingBatchCount() > 0;
  }

  function consumePendingBatchData() {
    batchDataGeneration += 1;
    leaveBatchHandled = true;

    if (Array.isArray(sdk.queue)) {
      sdk.queue.length = 0;
    } else {
      sdk.queue = [];
    }

    if (Array.isArray(sdk.cartSnapshots)) {
      sdk.cartSnapshots.length = 0;
    } else {
      sdk.cartSnapshots = [];
    }
  }

  function sendLeaveBatch() {
    if (leaveBatchHandled === true) {
      return false;
    }

    if (canSendBatch() !== true || hasPendingBatchData() !== true) {
      return false;
    }

    var apiUrl = getBatchApiUrl();
    var eventsToSend = Array.isArray(sdk.queue) ? sdk.queue.slice() : [];
    var cartSnapshotsToSend = Array.isArray(sdk.cartSnapshots) ? sdk.cartSnapshots.slice() : [];
    var payload = buildBatchPayload(eventsToSend, cartSnapshotsToSend);

    if (eventsToSend.length === 0 && cartSnapshotsToSend.length === 0) {
      return false;
    }

    if (typeof window.fetch !== 'function') {
      return false;
    }

    try {
      window.fetch(apiUrl, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
      });
      consumePendingBatchData();
      return true;
    } catch (error) {
      return false;
    }
  }

  function flushBatch() {
    if (batchFlushInFlight) {
      return Promise.resolve(null);
    }

    if (canSendBatch() !== true) {
      return Promise.resolve(null);
    }

    if (getPendingBatchCount() === 0) {
      return Promise.resolve(null);
    }

    var apiUrl = getBatchApiUrl();
    var eventsToSend = Array.isArray(sdk.queue) ? sdk.queue.slice() : [];
    var cartSnapshotsToSend = Array.isArray(sdk.cartSnapshots) ? sdk.cartSnapshots.slice() : [];
    var generationAtStart = batchDataGeneration;

    if (eventsToSend.length === 0 && cartSnapshotsToSend.length === 0) {
      return Promise.resolve(null);
    }

    batchFlushInFlight = true;
    var abortController;
    var fetchTimeout;
    var fetchRequest;

    try {
      abortController = new window.AbortController();
      fetchTimeout = window.setTimeout(function () {
        abortController.abort();
      }, batchFetchTimeoutMs);
      fetchRequest = window.fetch(apiUrl, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildBatchPayload(eventsToSend, cartSnapshotsToSend)),
        signal: abortController.signal,
      });
    } catch (error) {
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
      batchFlushInFlight = false;
      return Promise.resolve(null);
    }

    return fetchRequest
      .then(
        function (response) {
          if (response && response.ok === true && generationAtStart === batchDataGeneration) {
            removeSentPrefix(sdk.queue, eventsToSend.length);
            removeSentPrefix(sdk.cartSnapshots, cartSnapshotsToSend.length);
          }

          return response;
        },
        function () {
          return null;
        }
      )
      .finally(function () {
        clearTimeout(fetchTimeout);
        batchFlushInFlight = false;
      });
  }

  sdk.flush = function () {
    return flushBatch();
  };

  sdk.getContext = function () {
    return window.__ZASUWA_CTX__ || {};
  };

  sdk.getVisitorId = function () {
    return resolveVisitorId();
  };

  sdk.isAnalyticsAllowed = function () {
    return sdk.analyticsAllowed === true;
  };

  sdk.isEventEnabled = function (eventName) {
    var context = sdk.getContext();
    var config = context.config || {};
    var events = config.events || {};

    return config.enabled === true && events[eventName] === true;
  };

  sdk.eventId = function () {
    return generateUuid();
  };

  sdk.enqueue = function (event) {
    sdk.queue.push(event);
    if (getPendingBatchCount() >= batchFlushThreshold) {
      flushBatch();
    }
    ensureBatchTimer();
    return event;
  };

  sdk.getQueue = function () {
    return sdk.queue.map(function (event) {
      return Object.assign({}, event);
    });
  };

  sdk.getCartSnapshots = function () {
    if (!Array.isArray(sdk.cartSnapshots)) {
      return [];
    }

    return sdk.cartSnapshots.map(function (snapshot) {
      return Object.assign({}, snapshot);
    });
  };

  function bindLeaveBatchListeners() {
    if (typeof document.addEventListener !== 'function') {
      return;
    }

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        leaveBatchHandled = false;
        return;
      }

      if (document.visibilityState === 'hidden') {
        sendLeaveBatch();
      }
    });

    window.addEventListener('pagehide', function () {
      sendLeaveBatch();
    });
  }

  sdk.createEvent = function (eventName, payload) {
    var context = sdk.getContext();
    var config = context.config || {};
    var event = {
      event_id: sdk.eventId(),
      event_name: eventName,
      event_time: new Date().toISOString(),
      page_view_id: sdk.pageViewId,
      page_type: context.page_type,
      page_path: context.page_path,
    };

    if (payload && typeof payload === 'object') {
      if (payload.product_id !== undefined) {
        event.product_id = payload.product_id;
      }

      if (payload.product_handle !== undefined) {
        event.product_handle = payload.product_handle;
      }

      if (payload.module_type !== undefined) {
        event.module_type = payload.module_type;
      }

      if (payload.module_instance_id !== undefined) {
        event.module_instance_id = payload.module_instance_id;
      }

      if (config.fields && config.fields.variant_id === true && payload.variant_id !== undefined) {
        event.variant_id = payload.variant_id;
      }

      if (config.fields && config.fields.sku === true && payload.sku !== undefined) {
        event.sku = payload.sku;
      }
    }

    return event;
  };

  function getProductNodeData(node) {
    return {
      product_id: node.getAttribute('data-product-id'),
      product_handle: node.getAttribute('data-product-handle'),
      variant_id: node.getAttribute('data-variant-id'),
      sku: node.getAttribute('data-sku'),
    };
  }

  function getModuleContext(node) {
    var moduleRoot = node.closest('[data-zasuwa-module]');

    if (!moduleRoot) {
      return null;
    }

    return {
      module_type: moduleRoot.getAttribute('data-module-type'),
      module_instance_id: moduleRoot.getAttribute('data-module-instance-id'),
    };
  }

  function getRequestUrl(input) {
    if (typeof input === 'string') {
      return input;
    }

    if (input && typeof input.url === 'string') {
      return input.url;
    }

    return '';
  }

  function getRequestPath(input) {
    var requestUrl = getRequestUrl(input);

    if (!requestUrl) {
      return '';
    }

    try {
      return new URL(requestUrl, window.location.href).pathname;
    } catch (error) {
      return '';
    }
  }

  function normalizeCartMutationPath(requestPath) {
    if (!requestPath) {
      return '';
    }

    if (requestPath === '/cart.js') {
      return requestPath;
    }

    if (requestPath.slice(-3) === '.js') {
      return requestPath.slice(0, -3);
    }

    return requestPath;
  }

  function isTrackedCartMutation(input) {
    var requestPath = normalizeCartMutationPath(getRequestPath(input));

    return requestPath === '/cart/add' || requestPath === '/cart/change' || requestPath === '/cart/update';
  }

  function pushCartSnapshot(snapshot) {
    if (!Array.isArray(sdk.cartSnapshots)) {
      sdk.cartSnapshots = [];
    }

    sdk.cartSnapshots.push(snapshot);

    while (sdk.cartSnapshots.length > 20) {
      sdk.cartSnapshots.shift();
    }

    if (getPendingBatchCount() >= batchFlushThreshold) {
      flushBatch();
    }

    ensureBatchTimer();
  }

  function buildCartSnapshot(cart) {
    var items = Array.isArray(cart && cart.items) ? cart.items : [];

    return {
      snapshot_id: generateUuid(),
      snapshot_at: new Date().toISOString(),
      page_view_id: sdk.pageViewId,
      cart_token: cart && cart.token !== undefined ? cart.token : null,
      item_count: cart && cart.item_count !== undefined ? cart.item_count : 0,
      items: items.map(function (item) {
        return {
          key: item && item.key !== undefined ? item.key : null,
          product_id: item && item.product_id !== undefined ? item.product_id : null,
          variant_id: item && item.variant_id !== undefined ? item.variant_id : null,
          sku: item && item.sku !== undefined ? item.sku : null,
          quantity: item && item.quantity !== undefined ? item.quantity : 0,
        };
      }),
    };
  }

  function fetchCartSnapshot() {
    if (sdk.isEventEnabled('cart') !== true || typeof window.fetch !== 'function') {
      return Promise.resolve(null);
    }

    return window
      .fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
      })
      .then(function (response) {
        if (!response || response.ok !== true) {
          return null;
        }

        return response.json().then(function (cart) {
          var snapshot = buildCartSnapshot(cart);
          pushCartSnapshot(snapshot);
          return snapshot;
        });
      })
      .catch(function () {
        return null;
      });
  }

  function scheduleCartSnapshotRefresh() {
    if (sdk.isEventEnabled('cart') !== true) {
      return;
    }

    if (cartRefreshTimer) {
      clearTimeout(cartRefreshTimer);
    }

    cartRefreshTimer = window.setTimeout(function () {
      cartRefreshTimer = null;
      fetchCartSnapshot();
    }, 300);
  }

  function installFetchWrapper() {
    if (cartFetchWrapped || sdk.isEventEnabled('cart') !== true || typeof window.fetch !== 'function') {
      return;
    }

    var originalFetch = window.fetch;

    window.fetch = function () {
      var requestInput = arguments[0];
      var fetchPromise = originalFetch.apply(this, arguments);

      if (isTrackedCartMutation(requestInput)) {
        fetchPromise.then(function (response) {
          if (response && response.ok === true) {
            scheduleCartSnapshotRefresh();
          }
        });
      }

      return fetchPromise;
    };

    cartFetchWrapped = true;
  }

  function handleProductClick(event) {
    var target = event.target;

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    var link = target.closest('[data-zasuwa-product-link]');

    if (!link || !link.closest) {
      return;
    }

    var productRoot = link.closest('[data-zasuwa-product]');
    var moduleRoot = productRoot && productRoot.closest ? productRoot.closest('[data-zasuwa-module]') : null;

    if (!productRoot || !moduleRoot) {
      return;
    }

    var productData = getProductNodeData(productRoot);
    var moduleData = getModuleContext(productRoot);

    if (!moduleData || !moduleData.module_instance_id || !moduleData.module_type || !productData.product_id || !productData.product_handle) {
      return;
    }

    sdk.enqueue(
      sdk.createEvent('product_click', {
        product_id: productData.product_id,
        product_handle: productData.product_handle,
        module_type: moduleData.module_type,
        module_instance_id: moduleData.module_instance_id,
        variant_id: productData.variant_id,
        sku: productData.sku,
      })
    );
  }

  function bindProductClickListener() {
    if (productClickListenerBound || sdk.isEventEnabled('product_click') !== true) {
      return;
    }

    document.addEventListener('click', handleProductClick, true);
    productClickListenerBound = true;
  }

  function clearProductState(node) {
    var state = productStates.get(node);

    if (!state) {
      return;
    }

    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }

    state.visible = false;
    state.ratio = 0;
    state.eligible = false;
  }

  function registerProduct(node) {
    if (observedProducts.has(node)) {
      return;
    }

    observedProducts.add(node);
    productStates.set(node, {
      timer: null,
      visible: false,
      ratio: 0,
      eligible: false,
      fired: false,
    });

    if (productObserver) {
      productObserver.observe(node);
    }
  }

  function enqueueProductImpression(node) {
    var productData = getProductNodeData(node);
    var moduleData = getModuleContext(node);

    if (!moduleData || !moduleData.module_instance_id || !moduleData.module_type || !productData.product_id || !productData.product_handle) {
      clearProductState(node);
      if (productObserver) {
        productObserver.unobserve(node);
      }
      return false;
    }

    var key = String(productData.product_id) + '|' + String(moduleData.module_instance_id);

    if (productImpressionKeys.has(key)) {
      return;
    }

    productImpressionKeys.add(key);
    sdk.enqueue(
      sdk.createEvent('product_impression', {
        product_id: productData.product_id,
        product_handle: productData.product_handle,
        module_type: moduleData.module_type,
        module_instance_id: moduleData.module_instance_id,
        variant_id: productData.variant_id,
        sku: productData.sku,
      })
    );

    clearProductState(node);
    if (productObserver) {
      productObserver.unobserve(node);
    }

    return true;
  }

  function handleProductObserver(entries) {
    entries.forEach(function (entry) {
      var node = entry.target;
      var state = productStates.get(node);

      if (!state) {
        return;
      }

      state.visible = entry.isIntersecting === true;
      state.ratio = entry.intersectionRatio || 0;
      state.eligible = state.visible && state.ratio >= 0.5;

      if (state.fired) {
        return;
      }

      if (!state.eligible) {
        if (state.timer) {
          clearTimeout(state.timer);
          state.timer = null;
        }
        return;
      }

      if (state.timer) {
        return;
      }

      state.timer = window.setTimeout(function () {
        state.timer = null;

        if (!state.fired && state.eligible && node.isConnected) {
          if (enqueueProductImpression(node)) {
            state.fired = true;
          }
        }
      }, 500);
    });
  }

  function createProductObserver() {
    if (productObserver || sdk.isEventEnabled('product_impression') !== true) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      return;
    }

    productObserver = new IntersectionObserver(handleProductObserver, {
      threshold: [0, 0.5],
    });
  }

  function scanProducts(root) {
    if (sdk.isEventEnabled('product_impression') !== true) {
      return;
    }

    createProductObserver();

    if (!productObserver) {
      return;
    }

    var scope = root && root.querySelectorAll ? root : document;
    var products = scope.querySelectorAll('[data-zasuwa-product]');

    products.forEach(function (node) {
      registerProduct(node);
    });
  }

  sdk.scanProducts = scanProducts;
  sdk.getObservedProductCount = function () {
    return productImpressionKeys.size;
  };

  sdk.init = function () {
    if (sdk.__bootstrapped) {
      return sdk;
    }

    sdk.__bootstrapped = true;
    sdk.__initialized = true;
    sdk.queue = Array.isArray(sdk.queue) ? sdk.queue : [];
    sdk.analyticsAllowed = false;
    sdk.pageViewId = null;
    sdk.visitorId = sdk.visitorId || null;

    var context = sdk.getContext();
    var config = context.config || {};

    sdk.visitorId = resolveVisitorId();
    bindPrivacyListeners();
    requestPrivacyApi();

    if (config.enabled !== true) {
      return sdk;
    }

    sdk.pageViewId = generateUuid();

    if (sdk.isEventEnabled('page_view')) {
      sdk.enqueue(sdk.createEvent('page_view'));
    }

    if (context.page_type === 'product' && sdk.isEventEnabled('pdp_view')) {
      sdk.enqueue(
        sdk.createEvent('pdp_view', {
          product_id: context.product_id,
          product_handle: context.product_handle,
          variant_id: context.variant_id,
          sku: context.sku,
        })
      );
    }

    if (sdk.isEventEnabled('product_impression')) {
      createProductObserver();

      if (document.readyState === 'loading') {
        if (initialScanScheduled !== true) {
          initialScanScheduled = true;
          document.addEventListener(
            'DOMContentLoaded',
            function () {
              scanProducts(document);
            },
            { once: true }
          );
        }
      } else {
        scanProducts(document);
      }
    }

    if (sdk.isEventEnabled('product_click')) {
      bindProductClickListener();
    }

    if (sdk.isEventEnabled('cart')) {
      if (!Array.isArray(sdk.cartSnapshots)) {
        sdk.cartSnapshots = [];
      }

      installFetchWrapper();

      if (!cartBaselineStarted) {
        cartBaselineStarted = true;
        fetchCartSnapshot();
      }
    }

    if (getBatchApiUrl()) {
      ensureBatchTimer();
    }

    bindLeaveBatchListeners();

    return sdk;
  };

  window.ZasuwaInsights = sdk;
  sdk.init();
})();
